import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  PackageSearch, Loader2, Search, CheckCircle2, Circle, Truck, Package,
  Home, CreditCard, ClipboardList, Copy, ExternalLink, XCircle,
} from 'lucide-react';

const STEP_META = {
  placed: { label: 'Order Placed', icon: ClipboardList },
  confirmed: { label: 'Payment Confirmed', icon: CreditCard },
  preparing: { label: 'Preparing Your Order', icon: Package },
  shipped: { label: 'Shipped', icon: Truck },
  delivered: { label: 'Delivered', icon: Home },
};

export default function TrackOrderPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const search = async (code) => {
    const query = (code ?? q).trim();
    if (query.length < 4) { toast.error('Enter your order number'); return; }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.get('/orders/track', { params: { q: query } });
      setResult(r.data);
      setParams({ q: query }, { replace: true });
    } catch (e) {
      setError(e.response?.data?.detail || 'Lookup failed — please try again');
    } finally {
      setLoading(false);
    }
  };

  // Auto-search when arriving with ?q=
  useEffect(() => {
    const initial = params.get('q');
    if (initial && initial.trim().length >= 4) search(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyTracking = () => {
    navigator.clipboard?.writeText(result.tracking_number);
    toast.success('Tracking number copied');
  };

  return (
    <Layout>
      <section className="bg-[#F7F9F8] border-b border-[#E6E9EE]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 md:py-18">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#C8922F] font-semibold">
            <PackageSearch className="h-4 w-4" /> Royal Palm Store
          </div>
          <h1 data-testid="track-page-title" className="mt-3 font-headline text-4xl md:text-5xl text-[#17352D] tracking-[-0.02em]">
            Track Your Order
          </h1>
          <p className="mt-3 text-slate-700">Enter your order number to see delivery status. It's on your confirmation page and in your emails.</p>
          <div className="mt-6 flex gap-2">
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') search(); }}
              placeholder="e.g. 3B2EE1DA or RPS-1234567890"
              className="rounded-xl h-12 bg-white flex-1"
              data-testid="track-input"
            />
            <Button onClick={() => search()} disabled={loading} className="rounded-xl h-12 px-6 bg-[#17352D] hover:bg-[#1E4338] text-white" data-testid="track-search-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1.5" /> Track</>}
            </Button>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10 min-h-[300px]">
        {error && (
          <div className="rounded-2xl border border-rose-100 bg-rose-50/60 p-8 text-center" data-testid="track-error">
            <XCircle className="h-10 w-10 text-rose-400 mx-auto mb-3" />
            <div className="font-headline text-xl text-[#17352D]">Order not found</div>
            <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">{error}</p>
            <Button asChild variant="outline" className="mt-5 rounded-xl">
              <Link to="/contact" data-testid="track-contact-btn">Contact Support</Link>
            </Button>
          </div>
        )}

        {result && (
          <div data-testid="track-result">
            {/* Header card */}
            <div className="rounded-2xl border border-[#E6E9EE] bg-white p-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs text-slate-500">Order</div>
                <div className="font-headline text-2xl text-[#17352D]" data-testid="track-order-ref">#{result.order_ref}</div>
                {result.email_hint && <div className="text-xs text-slate-500 mt-0.5">for {result.email_hint}</div>}
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500">Placed</div>
                <div className="text-sm font-semibold text-[#17352D]">{result.placed_at ? new Date(result.placed_at).toLocaleDateString() : '—'}</div>
                {result.destination && <div className="text-xs text-slate-500 mt-0.5">Shipping to {result.destination}</div>}
              </div>
            </div>

            {/* Cancelled banner or progress steps */}
            {result.step === -1 ? (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
                <XCircle className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                <div className="font-headline text-xl text-[#17352D]">This order was cancelled</div>
                <p className="text-sm text-slate-600 mt-1">If that's unexpected, please contact support and we'll sort it out.</p>
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-[#E6E9EE] bg-white p-6">
                <div className="space-y-0">
                  {result.steps.map((s, idx) => {
                    const meta = STEP_META[s];
                    const Icon = meta.icon;
                    const done = idx <= result.step;
                    const current = idx === result.step;
                    return (
                      <div key={s} className="flex gap-4" data-testid={`track-step-${s}`}>
                        <div className="flex flex-col items-center">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center border-2 ${done ? 'bg-[#17352D] border-[#17352D] text-white' : 'bg-white border-slate-200 text-slate-300'}`}>
                            {done && !current ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                          </div>
                          {idx < result.steps.length - 1 && (
                            <div className={`w-0.5 h-8 ${idx < result.step ? 'bg-[#17352D]' : 'bg-slate-200'}`} />
                          )}
                        </div>
                        <div className="pt-2">
                          <div className={`text-sm font-semibold ${done ? 'text-[#17352D]' : 'text-slate-400'}`}>
                            {meta.label}
                            {current && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#FBF7EE] text-[#B27F26] border border-[#C8922F]/40 rounded-full px-2 py-0.5">Current</span>}
                          </div>
                          {s === 'shipped' && result.tracking_number && done && (
                            <div className="mt-1 flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-slate-600">{result.carrier ? `${result.carrier} · ` : ''}<span className="font-mono font-semibold text-[#17352D]" data-testid="track-tracking-number">{result.tracking_number}</span></span>
                              <button onClick={copyTracking} className="inline-flex items-center gap-1 text-xs text-[#C8922F] hover:underline" data-testid="track-copy-btn">
                                <Copy className="h-3 w-3" /> Copy
                              </button>
                              <a
                                href={`https://t.17track.net/en#nums=${encodeURIComponent(result.tracking_number)}`}
                                target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-[#C8922F] hover:underline"
                                data-testid="track-carrier-link"
                              >
                                <ExternalLink className="h-3 w-3" /> Track with carrier
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Items */}
            <div className="mt-4 rounded-2xl border border-[#E6E9EE] bg-white p-6">
              <div className="font-headline text-lg text-[#17352D] mb-3">In this order</div>
              <div className="space-y-2">
                {result.items.map((i, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm" data-testid={`track-item-${idx}`}>
                    <div className="text-slate-700">
                      {i.name}
                      {i.variant && <span className="text-slate-400"> — {i.variant}</span>}
                    </div>
                    <div className="text-slate-500 whitespace-nowrap ml-3">× {i.quantity}</div>
                  </div>
                ))}
              </div>
              {typeof result.total === 'number' && (
                <div className="mt-4 pt-3 border-t border-[#E6E9EE] flex justify-between text-sm">
                  <span className="font-semibold text-[#17352D]">Total paid</span>
                  <span className="font-headline text-lg text-[#17352D]">${result.total.toFixed(2)}</span>
                </div>
              )}
            </div>

            <p className="mt-4 text-xs text-slate-500 text-center">
              Questions about this order? <Link to="/contact" className="text-[#C8922F] hover:underline">Contact support</Link> · <Link to="/returns" className="text-[#C8922F] hover:underline">7-day returns</Link>
            </p>
          </div>
        )}

        {!result && !error && !loading && (
          <div className="text-center text-slate-400 py-10" data-testid="track-empty">
            <Circle className="h-4 w-4 inline mr-2 opacity-0" />
            Your order status will appear here.
          </div>
        )}
      </section>
    </Layout>
  );
}
